import { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { Plus, Trash, XCircle } from 'react-bootstrap-icons';
import { Organization, CreateOrganizationRequest } from '../../types/admin';
import { fetchOrganizations, createOrganization, deleteOrganization } from '../../api/admin';
import { useAuth } from 'react-oidc-context';
import { useAdmin } from '../../contexts/AdminContext';
import { formatDateTime } from '../../utils/dateFormatters';

export default function Organizations() {
  const auth = useAuth();
  const { isSuperAdmin } = useAdmin();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  
  // Form states
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDescription, setNewOrgDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchOrganizations(auth.user?.id_token);
      setOrganizations(response.data.organizations);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
      setError(err.response?.data?.error || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user?.id_token) {
      loadOrganizations();
    }
  }, [auth.user?.id_token]);

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newOrgName.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const data: CreateOrganizationRequest = {
        organization_name: newOrgName.trim(),
        metadata: newOrgDescription.trim() ? { description: newOrgDescription.trim() } : undefined
      };
      
      await createOrganization(data, auth.user?.id_token);
      
      setSuccess(`Organization "${newOrgName}" created successfully`);
      setShowCreateModal(false);
      setNewOrgName('');
      setNewOrgDescription('');
      loadOrganizations();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const initiateDelete = (org: Organization) => {
    setSelectedOrg(org);
    setShowDeleteModal(true);
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;

    try {
      setSubmitting(true);
      setError(null);
      
      await deleteOrganization(selectedOrg.organization_id, auth.user?.id_token);
      
      setSuccess(`Organization "${selectedOrg.organization_name}" deleted successfully`);
      setShowDeleteModal(false);
      setSelectedOrg(null);
      loadOrganizations();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete organization';
      setError(errorMsg);
      // Don't close modal on error, show error in modal
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Organization Management</h2>
          <p className="text-muted">Manage organizations and their settings</p>
        </div>
        {isSuperAdmin && (
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="me-2" />
            Add Organization
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {!isSuperAdmin && (
        <Alert variant="info">
          <strong>Note:</strong> You can view organizations but only Super Admins can create or delete them.
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading organizations...</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Organization ID</th>
              <th>Status</th>
              <th>Users</th>
              <th>Created Date</th>
              <th>Created By</th>
              {isSuperAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org.organization_id}>
                <td>
                  <strong>{org.organization_name}</strong>
                  {org.metadata?.description && (
                    <div className="text-muted small">{org.metadata.description}</div>
                  )}
                </td>
                <td>
                  <code className="small">{org.organization_id}</code>
                </td>
                <td>
                  <Badge bg={org.status === 'active' ? 'success' : 'secondary'}>
                    {org.status}
                  </Badge>
                </td>
                <td>{org.user_count || 0}</td>
                <td>{formatDateTime(org.created_date)}</td>
                <td>{org.created_by}</td>
                {isSuperAdmin && (
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => initiateDelete(org)}
                      disabled={org.user_count > 0}
                      title={org.user_count > 0 ? 'Cannot delete organization with users' : 'Delete organization'}
                    >
                      <Trash />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 7 : 6} className="text-center">
                  No organizations found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Create Organization Modal */}
      <Modal show={showCreateModal} onHide={() => !submitting && setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Organization</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateOrg}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Organization Name *</Form.Label>
              <Form.Control
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Enter organization name"
                required
                disabled={submitting}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newOrgDescription}
                onChange={(e) => setNewOrgDescription(e.target.value)}
                placeholder="Enter a brief description"
                disabled={submitting}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Organization'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Organization Modal */}
      <Modal show={showDeleteModal} onHide={() => !submitting && setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the organization:</p>
          <p className="fw-bold">{selectedOrg?.organization_name}</p>
          
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
          
          <Alert variant="warning" className="mt-3">
            <strong>Warning:</strong> This action cannot be undone. The organization will be marked as inactive.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteOrg} disabled={submitting}>
            {submitting ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

